import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, FileText, BookOpen, Mail, LogOut, ExternalLink, Inbox } from 'lucide-react';
import api from '../../lib/adminApi';
import WeddingsManager from '../../components/admin/WeddingsManager';
import ArticlesManager from '../../components/admin/ArticlesManager';
import IssueManager from '../../components/admin/IssueManager';
import NewsletterManager from '../../components/admin/NewsletterManager';
import EnquiriesManager from '../../components/admin/EnquiriesManager';

const TABS = [
  { id: 'weddings', label: 'Wedding Stories', icon: Heart },
  { id: 'articles', label: 'Articles', icon: FileText },
  { id: 'issue', label: 'Latest Issue', icon: BookOpen },
  { id: 'newsletter', label: 'Newsletter', icon: Mail },
  { id: 'enquiries', label: 'Vendor Enquiries', icon: Inbox },
];

export default function AdminDashboardPage() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('weddings');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => navigate('/admin/login'));
    api.get('/api/admin/stats').then((res) => setStats(res.data)).catch((err) => console.error('Failed to load stats:', err));
  }, [navigate]);

  const handleLogout = async () => {
    try { await api.post('/api/auth/logout'); } catch (e) { /* ignore */ }
    localStorage.removeItem('ido_admin_token');
    navigate('/admin/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#DE92A9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E6E2DC]/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-light" style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}>I DO</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#757B7F]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {stats && (
              <div className="hidden md:flex items-center gap-4 text-xs text-[#757B7F]" data-testid="admin-stats">
                <span><strong className="text-[#2C2C2C]">{stats.weddings}</strong> weddings</span>
                <span><strong className="text-[#2C2C2C]">{stats.articles}</strong> articles</span>
                <span><strong className="text-[#2C2C2C]">{stats.subscribers}</strong> subscribers</span>
              </div>
            )}
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#757B7F] hover:text-[#DE92A9] inline-flex items-center gap-1" data-testid="admin-view-site">
              View site <ExternalLink size={12} />
            </a>
            <button onClick={handleLogout} className="text-xs text-[#757B7F] hover:text-[#DE92A9] inline-flex items-center gap-1" data-testid="admin-logout-btn">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 text-xs tracking-wider font-medium py-3 border-b-2 whitespace-nowrap transition-colors ${
                tab === t.id ? 'border-[#DE92A9] text-[#DE92A9]' : 'border-transparent text-[#757B7F] hover:text-[#DE92A9]'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'weddings' && <WeddingsManager />}
        {tab === 'articles' && <ArticlesManager />}
        {tab === 'issue' && <IssueManager />}
        {tab === 'newsletter' && <NewsletterManager />}
        {tab === 'enquiries' && <EnquiriesManager />}
      </div>
    </div>
  );
}
